import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
  WritableSignal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { form, FormField, required, validate } from '@angular/forms/signals';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSliderModule } from '@angular/material/slider';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  DpeState,
  GouvDpeDto,
  PropertyDpeDto,
  PropertyDpeTechnicalDto,
  PropertyLocationDto,
} from '@workspace/data-types';
import { AbstractSignalFormComponent } from '@workspace/ng-aura';
import { take } from 'rxjs';
import { EtxeEnumDisplayPipe } from '../../../../pipes/etxe-enum-display.pipe';
import { PropertyDataCleaner } from '../../../../utils/property-data.cleaner';
import { PropertyDpeAutocompleteDialogComponent } from '../../dpe-autocomplete-dialog/dpe-autocomplete-dialog.component';
import { PropertyDpeAutocompleteComponent } from '../../dpe-autocomplete/dpe-autocomplete.component';
import {
  EnergyClassSelectorComponent,
  EnergyScaleType,
} from '../../energy-class-selector/energy-class-selector.component';

export interface PropertyDpeModel {
  state: DpeState;
  energyConsumption: number | null;
  greenhouseGasEmission: number | null;
  energyClass: string | null;
  climateClass: string | null;
  finalEnergyConsumption: number | null;
  estimatedCostMin: number | null;
  estimatedCostMax: number | null;
  priceIndexingDate: Date | null;
  realizationDate: Date | null;
  ademeNumber: string;
  technical?: PropertyDpeTechnicalDto | null;
}

@Component({
  selector: 'etxe-property-dpe-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FormField,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatDialogModule,
    MatSelectModule,
    EnergyClassSelectorComponent,
    PropertyDpeAutocompleteComponent,
    EtxeEnumDisplayPipe,
    MatSliderModule,
  ],
  templateUrl: './dpe-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyDpeFormComponent extends AbstractSignalFormComponent<PropertyDpeDto, PropertyDpeModel> {
  private dialog = inject(MatDialog);
  private isProcessingDpe = false;

  readonly disableClassCursorSelection = input<boolean>(true);
  readonly dpeStateEnum = DpeState;
  readonly dpeStates = Object.values(DpeState);

  propertyLocation = input<PropertyLocationDto>();
  readonly technicalExtracted = output<PropertyDpeTechnicalDto>();

  override model: WritableSignal<PropertyDpeModel> = signal<PropertyDpeModel>({
    state: DpeState.TO_DO,
    energyConsumption: null,
    greenhouseGasEmission: null,
    energyClass: null,
    climateClass: null,
    finalEnergyConsumption: null,
    estimatedCostMin: null,
    estimatedCostMax: null,
    priceIndexingDate: null,
    realizationDate: null,
    ademeNumber: '',
    technical: null,
  });

  override formTree = form(this.model, (b) => {
    required(b.state);
    required(b.energyConsumption, { when: () => this.model().state === DpeState.DONE });
    required(b.greenhouseGasEmission, { when: () => this.model().state === DpeState.DONE });
    required(b.energyClass, { when: () => this.model().state === DpeState.DONE });
    required(b.climateClass, { when: () => this.model().state === DpeState.DONE });
    required(b.realizationDate, { when: () => this.model().state === DpeState.DONE });
    required(b.ademeNumber, { when: () => this.model().state === DpeState.DONE });
    required(b.estimatedCostMin, { when: () => this.model().state === DpeState.DONE });
    required(b.estimatedCostMax, { when: () => this.model().state === DpeState.DONE });

    validate(b.ademeNumber, (ctx) => {
      if (this.model().state === DpeState.DONE) {
        const val = ctx.value()?.trim();
        if (!val) {
          return {
            kind: 'required',
            message: 'Le numéro ADEME est obligatoire.',
          };
        }
        if (!/^[0-9]{4}[A-Z][0-9]{7}[A-Z]$/.test(val)) {
          return {
            kind: 'pattern',
            message: 'Le format du numéro ADEME est invalide (ex. 1234A1234567B).',
          };
        }
      }
      return null;
    });

    validate(b.estimatedCostMin, (ctx) => {
      if (this.model().state === DpeState.DONE) {
        const val = ctx.value();
        if (val === null || val === undefined || val <= 0) {
          return {
            kind: 'requiredMinCost',
            message: 'Le coût annuel minimum doit être supérieur à 0 €.',
          };
        }
      }
      return null;
    });

    validate(b.estimatedCostMax, (ctx) => {
      if (this.model().state === DpeState.DONE) {
        const val = ctx.value();
        const minVal = this.model().estimatedCostMin;
        if (val === null || val === undefined || val <= 0) {
          return {
            kind: 'requiredMaxCost',
            message: 'Le coût annuel maximum doit être supérieur à 0 €.',
          };
        }
        if (minVal !== null && minVal !== undefined && val < minVal) {
          return {
            kind: 'invalidCostRange',
            message: 'Le coût maximum doit être supérieur ou égal au coût minimum.',
          };
        }
      }
      return null;
    });

    validate(b.priceIndexingDate, (ctx) => {
      const indexing = ctx.value();
      const realization = this.model().realizationDate;
      if (indexing && realization && new Date(indexing).getTime() > new Date(realization).getTime()) {
        return {
          kind: 'invalidPriceIndexingDate',
          message:
            "La date d'indexation des prix doit être antérieure ou égale à la date de réalisation du DPE.",
        };
      }
      return null;
    });
  });

  maxPriceIndexingDate = computed(() => this.model().realizationDate || new Date());

  isDpeDone = computed(() => this.model().state === DpeState.DONE);

  hasAdemeData = computed(() => {
    const dpe = this.model();
    return (
      dpe.state === DpeState.DONE && !!dpe.ademeNumber && dpe.energyConsumption !== null && !!dpe.energyClass
    );
  });

  isCostInvalid = computed(() => {
    if (this.model().state !== DpeState.DONE) return false;
    const min = this.model().estimatedCostMin;
    const max = this.model().estimatedCostMax;
    return (
      min === null ||
      min === undefined ||
      min <= 0 ||
      max === null ||
      max === undefined ||
      max <= 0 ||
      max < min
    );
  });

  onDpeStateChange(state: DpeState) {
    if (state !== DpeState.DONE) {
      this.model.update((prev) => ({
        ...prev,
        state,
        energyClass: null,
        climateClass: null,
        energyConsumption: null,
        greenhouseGasEmission: null,
        finalEnergyConsumption: null,
        estimatedCostMin: null,
        estimatedCostMax: null,
        priceIndexingDate: null,
        realizationDate: null,
        ademeNumber: '',
        technical: null,
      }));
    } else {
      this.model.update((prev) => ({ ...prev, state }));
    }
  }

  onDpeFound(data: GouvDpeDto) {
    if (this.isProcessingDpe) return;
    this.isProcessingDpe = true;

    const location = this.propertyLocation();
    if (!location) {
      this.isProcessingDpe = false;
      return;
    }

    const address = location.address;
    const currentAddressFull = `${address?.street || ''}${address?.zipCode ? ', ' + address?.zipCode : ''}${address?.city ? ' ' + address?.city : ''}`;
    const ademeAddress = data.address.displayName;

    const normalize = (s: string) =>
      s
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim();

    const isZipMismatch =
      address?.zipCode && data.address.postalCode && address?.zipCode !== data.address.postalCode;
    const isCityMismatch =
      address?.city && data.address.city && normalize(address?.city) !== normalize(data.address.city);
    const isStreetMismatch =
      address?.street &&
      !normalize(ademeAddress).includes(normalize(address?.street)) &&
      !normalize(address?.street).includes(normalize(data.address.street || ''));

    if (isZipMismatch || isCityMismatch || isStreetMismatch) {
      const dialogRef = this.dialog.open(PropertyDpeAutocompleteDialogComponent, {
        data: {
          currentAddress: currentAddressFull,
          ademeAddress: ademeAddress,
          ademeNumber: this.model().ademeNumber,
        },
        width: '700px',
        maxWidth: '95vw',
      });

      dialogRef
        .afterClosed()
        .pipe(take(1))
        .subscribe((result: 'confirm' | 'cancel' | undefined) => {
          this.isProcessingDpe = false;
          if (result === 'confirm') {
            this.applyDpeData(data);
          }
        });
    } else {
      this.applyDpeData(data);
      this.isProcessingDpe = false;
    }
  }

  private applyDpeData(data: GouvDpeDto) {
    const energyClass = data.dpeGroup ? this.calculateEnergyGrade(data.dpeGroup) : null;
    const climateClass = data.ghgGroup ? this.calculateGhgGrade(data.ghgGroup) : null;
    const realizationDate = data.realizationDate ? new Date(data.realizationDate) : null;

    let defaultPriceIndexingDate: Date | null = null;
    if (realizationDate) {
      if (realizationDate >= new Date('2021-07-01')) {
        defaultPriceIndexingDate = new Date('2021-01-01');
      } else {
        defaultPriceIndexingDate = realizationDate;
      }
    }

    this.model.update((prev) => {
      let priceIndexingDate = prev.priceIndexingDate;
      if (!priceIndexingDate) {
        priceIndexingDate = defaultPriceIndexingDate;
      } else if (realizationDate && new Date(priceIndexingDate).getTime() > realizationDate.getTime()) {
        priceIndexingDate = defaultPriceIndexingDate;
      }

      return {
        ...prev,
        state: DpeState.DONE,
        energyConsumption: data.dpeGroup ?? null,
        greenhouseGasEmission: data.ghgGroup ?? null,
        climateClass: (data.ghgClass || climateClass) ?? null,
        finalEnergyConsumption: data.finalEnergyConsumption ?? null,
        energyClass: energyClass ?? null,
        ademeNumber: data.metadata?.numero_dpe || prev.ademeNumber || '',
        realizationDate,
        priceIndexingDate,
        technical: data.technical ?? prev.technical,
      };
    });

    if (data.technical) {
      this.technicalExtracted.emit(data.technical);
    }
  }

  calculateEnergyGrade(value: number | undefined | null): string | null {
    if (value === undefined || value === null) return null;
    if (value <= 70) return 'A';
    if (value <= 110) return 'B';
    if (value <= 180) return 'C';
    if (value <= 250) return 'D';
    if (value <= 330) return 'E';
    if (value <= 420) return 'F';
    return 'G';
  }

  calculateGhgGrade(value: number | undefined | null): string | null {
    if (value === undefined || value === null) return null;
    if (value <= 6) return 'A';
    if (value <= 11) return 'B';
    if (value <= 30) return 'C';
    if (value <= 50) return 'D';
    if (value <= 70) return 'E';
    if (value <= 100) return 'F';
    return 'G';
  }

  onDpeNumberChange(value: string | undefined) {
    this.model.update((m) => ({ ...m, ademeNumber: value ?? '' }));
  }

  setEnergyClass(grade: string | null | undefined, type: EnergyScaleType) {
    if (type === 'energy') {
      this.model.update((m) => ({ ...m, energyClass: grade ?? null }));
    } else {
      this.model.update((m) => ({ ...m, climateClass: grade ?? null }));
    }
  }

  onEnergyConsumptionChange(val: number | null) {
    const calcGrade = this.calculateEnergyGrade(val);
    this.model.update((m) => ({ ...m, energyConsumption: val, energyClass: calcGrade }));
  }

  onGhgEmissionChange(val: number | null) {
    const calcGrade = this.calculateGhgGrade(val);
    this.model.update((m) => ({ ...m, greenhouseGasEmission: val, climateClass: calcGrade }));
  }

  onCostMinChange(val: number | null) {
    this.model.update((m) => {
      const nextMin = val !== null && !Number.isNaN(val) ? Number(val) : null;
      let nextMax = m.estimatedCostMax;
      if (nextMin !== null && nextMax !== null && nextMax < nextMin) {
        nextMax = nextMin;
      }
      return {
        ...m,
        estimatedCostMin: nextMin,
        estimatedCostMax: nextMax,
      };
    });
  }

  onCostMaxChange(val: number | null) {
    this.model.update((m) => {
      const nextMax = val !== null && !Number.isNaN(val) ? Number(val) : null;
      let nextMin = m.estimatedCostMin;
      if (nextMax !== null && nextMin !== null && nextMin > nextMax) {
        nextMin = nextMax;
      }
      return {
        ...m,
        estimatedCostMin: nextMin,
        estimatedCostMax: nextMax,
      };
    });
  }

  getQualityBadgeClass(quality?: string | null): string {
    if (!quality) return 'bg-neutral-a2 text-neutral-11 border-neutral-a5';
    const q = quality.toLowerCase().trim();
    if (q.includes('très bonne') || q === 'tres bonne') {
      return 'bg-jade-a3 text-jade-12 border-jade-a7';
    }
    if (q.includes('bonne')) {
      return 'bg-jade-a2 text-jade-11 border-jade-a5';
    }
    if (q.includes('moyenne')) {
      return 'bg-amber-a2 text-amber-11 border-amber-a6';
    }
    if (q.includes('insuffisante')) {
      return 'bg-ruby-a2 text-ruby-11 border-ruby-a6';
    }
    return 'bg-neutral-a2 text-neutral-11 border-neutral-a5';
  }

  getQualityIcon(quality?: string | null): string {
    if (!quality) return 'help_outline';
    const q = quality.toLowerCase().trim();
    if (q.includes('bonne')) return 'check_circle';
    if (q.includes('moyenne')) return 'warning';
    if (q.includes('insuffisante')) return 'error_outline';
    return 'info';
  }

  protected override buildModelFromDto(dto: PropertyDpeDto): PropertyDpeModel {
    return {
      state: dto?.state || DpeState.TO_DO,
      energyConsumption: dto?.energyConsumption ?? null,
      greenhouseGasEmission: dto?.greenhouseGasEmission ?? null,
      energyClass: dto?.energyClass ?? null,
      climateClass: dto?.climateClass ?? null,
      finalEnergyConsumption: dto?.finalEnergyConsumption ?? null,
      estimatedCostMin: dto?.estimatedCostMin ?? null,
      estimatedCostMax: dto?.estimatedCostMax ?? null,
      priceIndexingDate: dto?.priceIndexingDate ? new Date(dto.priceIndexingDate) : null,
      realizationDate: dto?.realizationDate ? new Date(dto.realizationDate) : null,
      ademeNumber: dto?.ademeNumber ?? '',
      technical: dto?.technical ?? null,
    };
  }

  protected override buildDtoFromModel(model: PropertyDpeModel): PropertyDpeDto {
    if (model.state !== DpeState.DONE) {
      return {
        state: model.state,
      } as PropertyDpeDto;
    }

    const rawDto: PropertyDpeDto = {
      state: model.state,
      ademeNumber: model.ademeNumber?.trim() || undefined,
      estimatedCostMin: model.estimatedCostMin ?? undefined,
      estimatedCostMax: model.estimatedCostMax ?? undefined,
    };
    return PropertyDataCleaner.deepClean(rawDto) as PropertyDpeDto;
  }

  protected override isFormValid(): boolean {
    if (this.model().state === DpeState.DONE) {
      if (this.isCostInvalid() || !this.hasAdemeData()) {
        return false;
      }
    }
    return super.isFormValid();
  }
}
